import { getBrazilianStatus } from '../../../utils/detectBrazil';

export const parseMultiplasPrediction = (response, sport) => {
  if (!response) {
    return null;
  }

  try {
    console.log('[MultiplasParser] Parsing response for sport:', sport);
    console.log('[MultiplasParser] Raw response:', response);

    const bets = [];
    const lines = response.split('\n').filter(line => line.trim());

    let currentBet = null;
    let summary = '';
    let inSummary = false;

    for (const line of lines) {
      const cleanLine = line.trim();

      if (cleanLine.toLowerCase().includes('summary') || cleanLine.toLowerCase().includes('analysis')) {
        inSummary = true;
        continue;
      }

      if (inSummary) {
        summary += cleanLine + ' ';
        continue;
      }

      // Multiple regex patterns for flexibility
      const patterns = [
        /^(\d+)\.\s*\[?([^\]]+?)\]?:\s*(.+)/, // 1. [Bet Type]: selection
        /^(\d+)\.\s*([^:]+):\s*(.+)/, // 1. Bet Type: selection
        /^(\d+)\.\s+(.+?)\s+\(([^)]+)\)\s+-\s+(\d+\.?\d*%?)\s+(HIGH|MED|MEDIUM|LOW)/i, // 1. Selection (Bet Type) - XX.X% CONF (reversed format)
        /^(\d+)\.\s+(.+?)\s+-\s+(\d+\.?\d*%?)\s+(HIGH|MED|MEDIUM|LOW)/i, // 1. Selection - XX.X% CONF (NHL format without colon)
        /^(\d+)\)\s*([^:]+):\s*(.+)/, // 1) Bet Type: selection
        /^•\s*([^:]+):\s*(.+)/, // • Bet Type: selection
        /^-\s*([^:]+):\s*(.+)/, // - Bet Type: selection
      ];

      let betMatch = null;
      for (const pattern of patterns) {
        betMatch = cleanLine.match(pattern);
        if (betMatch) break;
      }

      if (betMatch) {
        if (currentBet) {
          bets.push(currentBet);
        }

        let betType = '';
        let selection = '';
        let numericConfidence = null;
        let confidence = 'MEDIUM';

        // Check if this is the reversed format: Selection (Bet Type) - XX% CONF
        // Pattern match groups: [1]=number, [2]=selection, [3]=betType, [4]=percentage, [5]=confidence
        // TODO-NFL-FIX: Properly detect reversed format by checking if group 3 looks like a percentage
        // Current bug: Assumes length >= 6 means reversed format, but doesn't validate the groups
        if (betMatch.length >= 6 && betMatch[3] && betMatch[4] && !betMatch[3].match(/\d+\.?\d*%?/)) {
          // Reversed format: "3. Pacers +6.5 (Alt Spread) - 75.2% HIGH"
          selection = betMatch[2].trim();
          betType = betMatch[3].trim();
          // TODO-NFL-FIX: Strip percentage sign immediately to prevent "72.8%%" display bug
          numericConfidence = String(betMatch[4]).replace(/%/g, '');
          confidence = betMatch[5].toUpperCase();
        } else {
          // Standard format: "1. Bet Type: Selection - XX% CONF"
          betType = betMatch[betMatch.length - 2] || betMatch[1];
          const rest = betMatch[betMatch.length - 1] || betMatch[2];

          // Split by common separators
          const separators = [' - ', ' | ', ' , ', ' • ', ' / '];
          let parts = [rest];

          for (const sep of separators) {
            if (rest.includes(sep)) {
              parts = rest.split(sep).map(p => p.trim());
              break;
            }
          }

          // First part is usually the selection
          // TODO-NFL-FIX: Don't split on numbers without %, only split on actual percentages
          selection = parts[0] || rest.split(/\s*-\s*\d+\.?\d*%/)[0].trim();

          // Look for percentage (multiple formats)
          const percentPatterns = [
            /(\d+\.?\d*)%/,  // 72.5%
            /\((\d+\.?\d*)%\)/, // (72.5%)
            /\[(\d+\.?\d*)%\]/, // [72.5%]
            /(\d+\.?\d*)\s*percent/i, // 72.5 percent
          ];

          for (const pattern of percentPatterns) {
            const match = rest.match(pattern);
            if (match) {
              // TODO-NFL-FIX: Strip percentage sign immediately after extraction
              numericConfidence = String(match[1]).replace(/%/g, '');
              break;
            }
          }

          // Look for confidence level (multiple formats)
          const confidencePatterns = [
            { pattern: /\b(HIGH|HI)\b/i, value: 'HIGH' },
            { pattern: /\b(LOW|LO)\b/i, value: 'LOW' },
            { pattern: /\b(MED|MEDIUM|MODERATE)\b/i, value: 'MEDIUM' },
            { pattern: /★★★/, value: 'HIGH' },
            { pattern: /★★/, value: 'MEDIUM' },
            { pattern: /★/, value: 'LOW' },
          ];

          for (const { pattern, value } of confidencePatterns) {
            if (rest.match(pattern)) {
              confidence = value;
              break;
            }
          }
        }

        // Generate numeric confidence if missing but confidence level exists
        if (!numericConfidence && confidence) {
          numericConfidence = generateNumericFromLevel(confidence);
        }

        // Clean up bet type - remove brackets and extra whitespace
        // TODO-NFL-FIX: Don't remove ALL parentheses - only wrapping ones, preserve content like "(X2)"
        betType = betType.trim().replace(/^\((.+)\)$/, '$1').trim();

        // Clean up selection - preserve meaningful parentheses like (X2), but trim whitespace
        // TODO-NFL-FIX: Remove any leaked confidence text from selection (e.g., "- 72.8% HIGH")
        // Only remove if it has % OR confidence level, don't remove bare numbers (they're part of the bet)
        selection = selection.replace(/\s*-?\s*\d+\.?\d*%\s*(HIGH|MED|MEDIUM|LOW)?\s*$/i, '').trim();

        // TODO-NFL-FIX: Validate betType doesn't contain selection data (numbers, spreads, totals)
        // If betType has numbers/spreads, extraction likely failed
        if (betType && betType.match(/[+-]\d+\.?\d*|over|under \d+/i)) {
          console.warn('[MultiplasParser] BetType contains selection data, using fallback:', betType);
          betType = 'Recommended Bet';
        }

        // Ensure we don't have empty fields
        if (!betType || betType === '') {
          betType = 'Recommended Bet';
        }
        if (!selection || selection === '') {
          selection = 'See details';
        }

        // TODO-NFL-FIX: Final cleanup - ensure numericConfidence is clean number string without '%' or other symbols
        if (numericConfidence) {
          numericConfidence = String(numericConfidence).replace(/[^0-9.]/g, '');
        }

        currentBet = {
          betType: betType,
          selection: selection,
          numericConfidence: numericConfidence,
          confidence: confidence,
          reasoning: ''
        };
      } else if (currentBet && cleanLine.length > 0 && !cleanLine.match(/^\d+\./)) {
        if (cleanLine.includes('Reasoning:') || cleanLine.includes('because') || cleanLine.includes('due to')) {
          currentBet.reasoning = cleanLine.replace(/^(Reasoning:|because|due to)/i, '').trim();
        }
      }
    }

    if (currentBet) {
      bets.push(currentBet);
    }

    const fallbackBets = bets.length === 0 ? parseFallbackFormat(response) : bets;

    console.log('[MultiplasParser] Parsed bets count:', fallbackBets.length);
    console.log('[MultiplasParser] Parsed bets:', fallbackBets);

    return {
      sport,
      bets: fallbackBets.slice(0, 5),
      summary: summary.trim() || generateSummary(fallbackBets, sport),
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error parsing multiplas prediction:', error);
    return {
      sport,
      bets: parseFallbackFormat(response).slice(0, 5),
      summary: 'Analysis complete. Review the bets above for optimal selections.',
      timestamp: new Date().toISOString()
    };
  }
};

const parseFallbackFormat = (response) => {
  const bets = [];
  const lines = response.split('\n').filter(line => line.trim());

  for (const line of lines) {
    const cleanLine = line.trim();

    if (cleanLine.match(/^\d+[.)]/)) {
      // Try to extract data more aggressively
      let betType = 'Recommended Bet';
      let selection = 'See details';
      let numericConfidence = null;
      let confidence = 'MEDIUM';

      // Check for reversed format first: "Selection (Bet Type) - XX% CONF"
      const reversedMatch = cleanLine.match(/^\d+[.)]\s+(.+?)\s+\(([^)]+)\)\s*-?\s*(\d+\.?\d*%?)?\s*(HIGH|MED|MEDIUM|LOW)?/i);
      if (reversedMatch) {
        selection = reversedMatch[1].trim();
        betType = reversedMatch[2].trim();
        // TODO-NFL-FIX: Strip percentage sign in fallback parser
        numericConfidence = reversedMatch[3]?.replace(/%/g, '');
        confidence = reversedMatch[4]?.toUpperCase() || 'MEDIUM';
      } else {
        // Standard format: "Bet Type: Selection - XX% CONF"
        const colonIndex = cleanLine.indexOf(':');

        if (colonIndex > 0) {
          // Split on first colon to preserve selections like "Panthers/Draw (X2)"
          betType = cleanLine.substring(0, colonIndex).replace(/^\d+[.)]/, '').trim();
          const afterColon = cleanLine.substring(colonIndex + 1).trim();

          // Find where the selection ends (usually at " - " before percentage)
          const dashIndex = afterColon.indexOf(' - ');
          if (dashIndex > 0) {
            selection = afterColon.substring(0, dashIndex).trim();
          } else {
            // TODO-NFL-FIX: Only split on percentage with %, not bare numbers
            selection = afterColon.split(/\s*\d+\.?\d*%/)[0].trim();
          }

          // Extract percentage if present
          const percentMatch = cleanLine.match(/(\d+\.?\d*)%/);
          if (percentMatch) {
            // TODO-NFL-FIX: Strip percentage sign in fallback parser
            numericConfidence = percentMatch[1].replace(/%/g, '');
          }

          // Extract confidence level
          const confMatch = cleanLine.match(/\b(HIGH|MED|MEDIUM|LOW)\b/i);
          if (confMatch) {
            confidence = confMatch[1].toUpperCase();
          }
        } else {
          // No colon found, try simple dash split
          const parts = cleanLine.split(/\s+-\s+/).map(p => p.trim());
          if (parts.length >= 2) {
            betType = parts[0].replace(/^\d+[.)]/, '').trim();
            // TODO-NFL-FIX: Only split on percentage with %, not bare numbers
            selection = parts[1].split(/\s*\d+\.?\d*%/)[0].trim();

            const percentMatch = cleanLine.match(/(\d+\.?\d*)%/);
            if (percentMatch) {
              // TODO-NFL-FIX: Strip percentage sign in fallback parser
              numericConfidence = percentMatch[1].replace(/%/g, '');
            }

            const confMatch = cleanLine.match(/\b(HIGH|MED|MEDIUM|LOW)\b/i);
            if (confMatch) {
              confidence = confMatch[1].toUpperCase();
            }
          }
        }
      }

      // Ensure we have valid values
      if (!betType || betType === '') {
        betType = 'Recommended Bet';
      }
      if (!selection || selection === '') {
        selection = 'See details';
      }

      bets.push({
        betType: betType || 'Recommended Bet',
        selection: selection,
        numericConfidence: numericConfidence,
        confidence: confidence === 'MED' ? 'MEDIUM' : confidence,
        reasoning: ''
      });
    }
  }

  if (bets.length === 0) {
    const defaultBets = [
      { betType: 'Spread', selection: 'Analyzing...', numericConfidence: null, confidence: 'MEDIUM', reasoning: '' },
      { betType: 'Total', selection: 'Analyzing...', numericConfidence: null, confidence: 'MEDIUM', reasoning: '' },
      { betType: 'Moneyline', selection: 'Analyzing...', numericConfidence: null, confidence: 'MEDIUM', reasoning: '' },
    ];
    return defaultBets;
  }

  return bets;
};

const generateSummary = (bets, sport) => {
  const isBrazilian = getBrazilianStatus();

  if (!bets || bets.length === 0) {
    return isBrazilian
      ? 'Análise completa. Revise as recomendações acima.'
      : 'Analysis complete. Review the recommendations above.';
  }

  const highConfidence = bets.filter(b => b.confidence === 'HIGH').length;
  const sportName = getSportName(sport);

  if (highConfidence > 0) {
    return isBrazilian
      ? `Encontradas ${highConfidence} oportunidades de apostas de alta confiança em ${sportName}. Estas seleções são baseadas na forma atual e análise estatística.`
      : `Found ${highConfidence} high-confidence ${sportName} betting opportunities. These selections are based on current form and statistical analysis.`;
  }

  return isBrazilian
    ? `Identificadas ${bets.length} oportunidades potenciais de apostas em ${sportName} com níveis de confiança moderados. Considere combinar para melhor valor.`
    : `Identified ${bets.length} potential ${sportName} betting opportunities with moderate confidence levels. Consider combining for better value.`;
};

const generateNumericFromLevel = (confidence) => {
  // Generate realistic confidence percentages for sports betting
  // In betting, even 60% is considered a strong edge (breakeven is ~52.4% with -110 odds)
  const ranges = {
    'HIGH': { min: 62, max: 72 },     // Strong plays with significant edge
    'MEDIUM': { min: 55, max: 61.9 }, // Decent edge above breakeven
    'LOW': { min: 51, max: 54.9 }     // Slight edge, near breakeven
  };

  const range = ranges[confidence] || ranges.MEDIUM;
  const base = Math.random() * (range.max - range.min) + range.min;
  return (Math.round(base * 10) / 10).toFixed(1);
};

const getSportName = (sport) => {
  const sportNames = {
    baseball: 'baseball',
    football: 'Futebol (Soccer)',
    nfl: 'NFL',
    ncaaf: 'NCAA Football',
    nba: 'NBA',
    nhl: 'NHL',
  };
  return sportNames[sport?.toLowerCase()] || 'sports';
};